import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    await this.#waitUntilNearViewport();
    if (!this.isConnected) return;

    await this.#randomizeProducts();
    if (!this.isConnected) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('pointerdown', this.#handleGalleryPointerDown);
    this.addEventListener('pointermove', this.#handleGalleryPointerMove);
    this.addEventListener('pointerup', this.#handleGalleryPointerUp);
    this.addEventListener('pointercancel', this.#clearGalleryPointer);
    this.addEventListener('click', this.#handleGalleryClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.#resolveVisibility?.();
    this.#animation?.cancel();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('pointerdown', this.#handleGalleryPointerDown);
    this.removeEventListener('pointermove', this.#handleGalleryPointerMove);
    this.removeEventListener('pointerup', this.#handleGalleryPointerUp);
    this.removeEventListener('pointercancel', this.#clearGalleryPointer);
    this.removeEventListener('click', this.#handleGalleryClick, true);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #galleryPointer = null;

  #blockGalleryClick = false;

  #visibilityObserver = null;

  #resolveVisibility = null;

  #waitUntilNearViewport() {
    if (window.Shopify?.designMode) return Promise.resolve();
    return new Promise((resolve) => {
      this.#resolveVisibility = resolve;
      this.#visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#visibilityObserver.disconnect();
        this.#visibilityObserver = null;
        this.#resolveVisibility = null;
        resolve();
      }, { rootMargin: '300px 0px' });
      this.#visibilityObserver.observe(this);
    });
  }

  #prepareCopy(clone, suffix) {
    clone.setAttribute('aria-hidden', 'true');
    this.#namespaceIds(clone, suffix);
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }

  async #randomizeProducts() {
    if (!this.hasAttribute('data-randomize-products') || window.Shopify?.designMode) return;

    const section = this.closest('[data-random-section-id]');
    const items = this.refs.marqueeItems[0];
    if (!section || !items) return;

    const sectionId = section.getAttribute('data-random-section-id');
    const pageCount = Number(section.getAttribute('data-random-page-count'));
    const itemCount = Number(section.getAttribute('data-random-item-count'));
    if (!sectionId || pageCount < 2 || itemCount < 1) {
      this.#shuffleItems(items);
      return;
    }

    const pool = Array.from(items.children);
    const requests = [];

    for (let page = 2; page <= pageCount; page++) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', `${page}`);
      requests.push(
        fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => this.#extractPageItems(html, page))
          .catch(() => [])
      );
    }

    const pageItems = await Promise.all(requests);
    for (const products of pageItems) pool.push(...products);

    this.#shuffle(pool);
    items.replaceChildren(...pool.slice(0, itemCount));
  }

  #extractPageItems(html, page) {
    if (!html) return [];

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const source = documentFragment.querySelector('.resource-list__marquee-items');
    if (!source) return [];

    return Array.from(source.children).map((slide, index) => {
      this.#namespaceIds(slide, `random-${page}-${index}`);
      return slide;
    });
  }

  #namespaceIds(root, suffix) {
    const idMap = new Map();

    for (const element of root.querySelectorAll('[id]')) {
      const oldId = element.id;
      const newId = `${oldId}-${suffix}`;
      idMap.set(oldId, newId);
      element.id = newId;
    }

    for (const element of root.querySelectorAll('[for], [form], [aria-controls], [aria-labelledby], [aria-describedby]')) {
      for (const attribute of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
        const value = element.getAttribute(attribute);
        if (!value) continue;
        element.setAttribute(
          attribute,
          value
            .split(' ')
            .map((id) => idMap.get(id) ?? id)
            .join(' ')
        );
      }
    }
  }

  #shuffle(items) {
    for (let index = items.length - 1; index > 0; index--) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
    }
  }

  #shuffleItems(container) {
    const items = Array.from(container.children);
    this.#shuffle(items);
    container.replaceChildren(...items);
  }

  #handleGalleryPointerDown = (event) => {
    if (!this.hasAttribute('data-product-gallery-touch-loop')) return;

    const gallery = event.target.closest('.card-gallery slideshow-component[infinite]');
    if (!gallery || !this.contains(gallery)) return;

    this.#galleryPointer = {
      gallery,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      x: event.clientX,
      y: event.clientY,
      dragged: false,
    };
  };

  #handleGalleryPointerMove = (event) => {
    const start = this.#galleryPointer;
    if (!start || event.pointerId !== start.pointerId) return;

    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (distance > 8) start.dragged = true;
  };

  #handleGalleryPointerUp = (event) => {
    const start = this.#galleryPointer;
    this.#galleryPointer = null;
    if (!start || event.pointerId !== start.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (start.dragged) {
      this.#blockGalleryClick = true;
      setTimeout(() => {
        this.#blockGalleryClick = false;
      }, 400);
    }

    if (start.pointerType !== 'touch') return;
    if (Math.abs(deltaX) < 35 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    event.preventDefault();
    if (deltaX < 0) {
      start.gallery.next();
    } else {
      start.gallery.previous();
    }

  };

  #clearGalleryPointer = () => {
    this.#galleryPointer = null;
  };

  #handleGalleryClick = (event) => {
    if (!this.#blockGalleryClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#slowDown();
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#speedUp();
  };

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    this.#prepareCopy(clone, 'marquee-loop');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      this.#prepareCopy(clone, `marquee-repeat-${i}`);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
